/**
 * Switch Version History Management Component
 *
 * Displays all historical versions of a switch with selective deletion capabilities.
 * Features:
 * - Table view of all versions with metadata
 * - Checkbox selection (current version disabled)
 * - Summary statistics
 * - Delete confirmation dialog
 * - Loading and error states
 */

import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { api } from '@/services/api';
import type { VersionSummary } from '@/types/entities';

interface SwitchVersionHistoryProps {
  hostname: string;
  onDeleted?: () => void;  // Callback after successful deletion
}

export const SwitchVersionHistory: FC<SwitchVersionHistoryProps> = ({ hostname, onDeleted }) => {
  const [versions, setVersions] = useState<VersionSummary[]>([]);
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getSwitchVersions(hostname);
      setVersions(response.versions);
      setCurrentVersionId(response.currentVersion);
      // Clear selection when refreshing
      setSelectedTransactionIds(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
  }, [hostname]);

  const handleToggleSelection = (transactionId: string) => {
    if (transactionId === currentVersionId) return; // Can't select current version

    setSelectedTransactionIds((prev) => {
      const next = new Set(prev);
      if (next.has(transactionId)) {
        next.delete(transactionId);
      } else {
        next.add(transactionId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const selectableVersions = versions.filter(v => v.transactionId !== currentVersionId);
    setSelectedTransactionIds(new Set(selectableVersions.map(v => v.transactionId)));
  };

  const handleSelectNone = () => {
    setSelectedTransactionIds(new Set());
  };

  const handleOpenDeleteDialog = () => {
    if (selectedTransactionIds.size === 0) return;
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      const result = await api.deleteSwitchVersions(
        hostname,
        Array.from(selectedTransactionIds)
      );

      setDeleteSuccess(result.message);
      setDeleteDialogOpen(false);

      // Refresh version list
      await fetchVersions();

      // Call parent callback
      if (onDeleted) {
        onDeleted();
      }

      // Clear success message after 5 seconds
      setTimeout(() => setDeleteSuccess(null), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete versions');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Calculate summary statistics
  const totalVersions = versions.length;
  const historicalVersions = versions.filter(v => v.transactionId !== currentVersionId).length;
  const selectedCount = selectedTransactionIds.size;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && versions.length === 0) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header with summary stats */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">
            Version History for {hostname}
          </Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchVersions} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Chip
            label={`Total Versions: ${totalVersions}`}
            color="default"
            size="small"
          />
          <Chip
            label={`Historical: ${historicalVersions}`}
            color="primary"
            size="small"
          />
          {selectedCount > 0 && (
            <Chip
              label={`Selected: ${selectedCount}`}
              color="secondary"
              size="small"
            />
          )}
        </Stack>
      </Box>

      {/* Success message */}
      {deleteSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDeleteSuccess(null)}>
          {deleteSuccess}
        </Alert>
      )}

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Selection controls */}
      {historicalVersions > 0 && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSelectAll}
              disabled={selectedCount === historicalVersions}
            >
              Select All Historical
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSelectNone}
              disabled={selectedCount === 0}
            >
              Clear Selection
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleOpenDeleteDialog}
              disabled={selectedCount === 0}
            >
              Delete Selected ({selectedCount})
            </Button>
          </Stack>
        </Box>
      )}

      {/* Version table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">Select</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Filename</TableCell>
              <TableCell>Upload Date</TableCell>
              <TableCell>Valid Period</TableCell>
              <TableCell>Uploader</TableCell>
              <TableCell align="right">Interfaces</TableCell>
              <TableCell align="right">VLANs</TableCell>
              <TableCell>Changes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {versions.map((version) => {
              const isCurrent = version.transactionId === currentVersionId;
              return (
                <TableRow
                  key={version.transactionId}
                  sx={{
                    backgroundColor: isCurrent ? 'action.selected' : 'inherit',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Tooltip
                      title={isCurrent ? 'Cannot delete current version' : ''}
                    >
                      <span>
                        <Checkbox
                          checked={selectedTransactionIds.has(version.transactionId)}
                          onChange={() => handleToggleSelection(version.transactionId)}
                          disabled={isCurrent}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {isCurrent ? (
                      <Chip label="Current" color="success" size="small" />
                    ) : (
                      <Chip label="Historical" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{version.filename}</TableCell>
                  <TableCell>{formatDate(version.uploadTimestamp)}</TableCell>
                  <TableCell>
                    {version.validFrom && (
                      <Typography variant="caption" display="block">
                        From: {formatDate(version.validFrom)}
                      </Typography>
                    )}
                    {version.validTo && (
                      <Typography variant="caption" display="block">
                        To: {formatDate(version.validTo)}
                      </Typography>
                    )}
                    {!version.validTo && (
                      <Typography variant="caption" color="success.main">
                        Active
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{version.uploaderUsername}</TableCell>
                  <TableCell align="right">{version.interfaceCount}</TableCell>
                  <TableCell align="right">{version.vlanCount}</TableCell>
                  <TableCell>
                    {version.changesFromPrevious ? (
                      <Box>
                        {version.changesFromPrevious.interfacesAdded > 0 && (
                          <Chip
                            label={`+${version.changesFromPrevious.interfacesAdded} iface`}
                            size="small"
                            color="success"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        )}
                        {version.changesFromPrevious.interfacesRemoved > 0 && (
                          <Chip
                            label={`-${version.changesFromPrevious.interfacesRemoved} iface`}
                            size="small"
                            color="error"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        )}
                        {version.changesFromPrevious.vlansAdded > 0 && (
                          <Chip
                            label={`+${version.changesFromPrevious.vlansAdded} VLAN`}
                            size="small"
                            color="success"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        )}
                        {version.changesFromPrevious.vlansRemoved > 0 && (
                          <Chip
                            label={`-${version.changesFromPrevious.vlansRemoved} VLAN`}
                            size="small"
                            color="error"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        )}
                        {!version.changesFromPrevious.interfacesAdded &&
                          !version.changesFromPrevious.interfacesRemoved &&
                          !version.changesFromPrevious.vlansAdded &&
                          !version.changesFromPrevious.vlansRemoved && (
                            <Typography variant="caption" color="text.secondary">
                              No changes
                            </Typography>
                          )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Initial version
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* No versions message */}
      {versions.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No version history found for this switch.
          </Typography>
        </Box>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Version Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to delete <strong>{selectedCount}</strong> historical version
            {selectedCount !== 1 ? 's' : ''} of switch <strong>{hostname}</strong>.
          </DialogContentText>

          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Summary:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Versions to delete: {selectedCount}</li>
              <li>Current version will be preserved</li>
              <li>This action cannot be undone</li>
            </ul>
          </Box>

          <Alert severity="warning" sx={{ mt: 2 }}>
            This will permanently delete the selected versions and their associated interface
            data from the graph database. The original configuration files will also be removed.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Versions'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
